/**
 * CommunicationTimeline - Reusable component for Phase 5.3B
 * Shows communication history for bookings or guests
 * 
 * Props:
 * - bookingId: string (for booking-specific timeline)
 * - guestId: string (for guest-wide timeline)
 * - limit: number (default 20)
 * - compact: boolean (default false)
 * - showQuickActions: boolean (default true)
 * - onMessageClick: function (optional callback when clicking on a message)
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../lib/api';
import { toast } from 'sonner';
import {
  MessageSquare, Bell, Mail, Phone, Clock, Check, AlertTriangle,
  ChevronDown, ChevronRight, RefreshCw, Copy, Send, Eye,
  ArrowRight, RotateCcw, Zap, Calendar, User
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

// Channel icons
const ChannelIcon = ({ channel, className = "w-4 h-4" }) => {
  switch (channel?.toLowerCase()) {
    case 'whatsapp':
      return <MessageSquare className={`${className} text-green-600`} />;
    case 'push':
      return <Bell className={`${className} text-purple-600`} />;
    case 'email':
      return <Mail className={`${className} text-blue-600`} />;
    case 'sms':
      return <Phone className={`${className} text-cyan-600`} />;
    default:
      return <MessageSquare className={`${className} text-gray-500`} />;
  }
};

// Status badge
const StatusBadge = ({ status }) => {
  const statusStyles = {
    simulated: 'bg-gray-100 text-gray-700 border-gray-300',
    scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
    queued: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    processing: 'bg-purple-50 text-purple-700 border-purple-200',
    accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    sent: 'bg-green-50 text-green-700 border-green-200',
    delivered: 'bg-green-100 text-green-800 border-green-300',
    read: 'bg-green-200 text-green-900 border-green-400',
    failed: 'bg-red-50 text-red-700 border-red-200',
    retry_pending: 'bg-orange-50 text-orange-700 border-orange-200',
    skipped: 'bg-gray-50 text-gray-600 border-gray-200',
    cancelled: 'bg-gray-100 text-gray-500 border-gray-300',
    fallback_started: 'bg-amber-50 text-amber-700 border-amber-200',
    routing_decision: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    delivery: 'bg-teal-50 text-teal-700 border-teal-200',
  };

  const statusLabels = {
    simulated: 'Simulato',
    scheduled: 'Programmato',
    queued: 'In coda',
    processing: 'In elaborazione',
    accepted: 'Accettato',
    sent: 'Inviato',
    delivered: 'Consegnato',
    read: 'Letto',
    failed: 'Fallito',
    retry_pending: 'Riprova in sospeso',
    skipped: 'Saltato',
    cancelled: 'Annullato',
    fallback_started: 'Fallback avviato',
    routing_decision: 'Routing',
    delivery: 'Delivery',
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${statusStyles[status] || statusStyles.queued}`}>
      {statusLabels[status] || status}
    </span>
  );
};

// Single timeline event
const TimelineEvent = ({ event, compact, onViewDetails, onCopy, onResend }) => {
  const [expanded, setExpanded] = useState(false);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const isManual = event.operator === 'admin' || !event.rule_name;
  const canResend = ['failed', 'cancelled', 'skipped'].includes(event.status);
  const hasPreview = event.rendered_message || event.message_preview;

  return (
    <div 
      className={`relative pl-8 pb-4 border-l-2 ${
        event.status === 'failed' ? 'border-red-200' : 
        event.status === 'sent' || event.status === 'delivered' || event.status === 'accepted' ? 'border-green-200' : 
        'border-gray-200'
      }`}
      data-testid={`timeline-event-${event.id}`}
    >
      {/* Timeline dot */}
      <div className={`absolute left-[-9px] top-0 w-4 h-4 rounded-full border-2 bg-white ${
        event.status === 'failed' ? 'border-red-400' : 
        event.status === 'sent' || event.status === 'delivered' || event.status === 'accepted' ? 'border-green-400' : 
        'border-gray-300'
      }`}>
        <div className={`w-2 h-2 m-[2px] rounded-full ${
          event.status === 'failed' ? 'bg-red-400' : 
          event.status === 'sent' || event.status === 'delivered' || event.status === 'accepted' ? 'bg-green-400' : 
          'bg-gray-300'
        }`} />
      </div>

      <div className={`bg-white rounded-lg border ${compact ? 'p-2' : 'p-3'} shadow-sm hover:shadow-md transition-shadow`}>
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <ChannelIcon channel={event.channel} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm text-gray-900 truncate">
                  {event.template_name || event.event_type || event.type || 'Messaggio'}
                </span>
                <StatusBadge status={event.status} />
                {isManual && (
                  <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                    Manuale
                  </span>
                )}
                {event.fallback_used && (
                  <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded flex items-center gap-1">
                    <ArrowRight className="w-3 h-3" />
                    Fallback
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                <Clock className="w-3 h-3 inline mr-1" />
                {formatDate(event.timestamp || event.scheduled_at || event.created_at)}
                {event.rule_name && (
                  <span className="ml-2">
                    <Zap className="w-3 h-3 inline mr-0.5" />
                    {event.rule_name}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Actions */}
          {!compact && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => onViewDetails?.(event)}
                title="Dettagli"
              >
                <Eye className="w-3.5 h-3.5" />
              </Button>
              {hasPreview && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => onCopy?.(event.rendered_message || event.message_preview)}
                  title="Copia messaggio"
                >
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              )}
              {canResend && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-orange-600 hover:text-orange-700"
                  onClick={() => onResend?.(event)}
                  title="Reinvia"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </Button>
              )}
              {hasPreview && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setExpanded(!expanded)}
                >
                  {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Expanded preview */}
        {expanded && hasPreview && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Anteprima:</p>
            <div className="text-sm text-gray-700 bg-gray-50 rounded p-2 whitespace-pre-wrap max-h-32 overflow-y-auto">
              {(event.rendered_message || event.message_preview || '').substring(0, 500)}
              {(event.rendered_message || event.message_preview || '').length > 500 && '...'}
            </div>
          </div>
        )}

        {/* Error info */}
        {event.status === 'failed' && event.last_error && (
          <div className="mt-2 pt-2 border-t border-red-100">
            <p className="text-xs text-red-600 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {event.last_error}
            </p>
          </div>
        )}

        {/* Retry info */}
        {event.retry_count > 0 && (
          <p className="text-xs text-orange-600 mt-1">
            Tentativi: {event.retry_count}
          </p>
        )}

        {/* Booking info for guest timeline */}
        {event.booking_code && (
          <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {event.booking_code}
            {event.booking_date && ` - ${event.booking_date}`}
          </div>
        )}
      </div>
    </div>
  );
};

// Message Details Modal
const MessageDetailsModal = ({ event, open, onClose, onResend }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    if (open && event?.id) {
      fetchDetails();
    }
  }, [open, event?.id]);

  const fetchDetails = async () => {
    if (!event?.id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/communication/message/${event.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setDetails(await res.json());
      }
    } catch (e) {
      console.error('Error fetching message details:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('it-IT');
    } catch {
      return dateStr;
    }
  };

  if (!event) return null;

  const data = details || event;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ChannelIcon channel={data.channel} className="w-5 h-5" />
            Dettagli Messaggio
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Status & Channel */}
            <div className="flex items-center gap-4 flex-wrap">
              <div>
                <p className="text-xs text-gray-500">Stato</p>
                <StatusBadge status={data.status} />
              </div>
              <div>
                <p className="text-xs text-gray-500">Canale</p>
                <p className="font-medium capitalize">{data.channel}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Tipo</p>
                <p className="font-medium">{data.operator === 'admin' ? 'Manuale' : 'Automatico'}</p>
              </div>
            </div>

            {/* Timestamps */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-500">Programmato</p>
                <p className="font-medium">{formatDate(data.scheduled_at)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Inviato</p>
                <p className="font-medium">{formatDate(data.processed_at || data.sent_at)}</p>
              </div>
              {data.execution_time_ms && (
                <div>
                  <p className="text-xs text-gray-500">Tempo esecuzione</p>
                  <p className="font-medium">{data.execution_time_ms}ms</p>
                </div>
              )}
            </div>

            {/* Booking & Guest */}
            {(data.booking || data.guest) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-gray-50 rounded-lg">
                {data.booking && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Prenotazione</p>
                    <p className="font-medium">{data.booking.codice_prenotazione || data.booking.code}</p>
                    <p className="text-sm text-gray-600">{data.booking.nome_ospite}</p>
                    {data.booking.data_arrivo && (
                      <p className="text-xs text-gray-500">{data.booking.data_arrivo} → {data.booking.data_partenza}</p>
                    )}
                  </div>
                )}
                {data.guest && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Ospite</p>
                    <p className="font-medium">{data.guest.nome} {data.guest.cognome}</p>
                    {data.guest.email && <p className="text-sm text-gray-600">{data.guest.email}</p>}
                    {data.guest.telefono && <p className="text-sm text-gray-600">{data.guest.telefono}</p>}
                  </div>
                )}
              </div>
            )}

            {/* Template & Rule */}
            {(data.template || data.rule_name || data.template_name) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(data.template || data.template_name) && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Template</p>
                    <p className="font-medium">{data.template?.name || data.template_name}</p>
                  </div>
                )}
                {data.rule_name && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Regola automazione</p>
                    <p className="font-medium">{data.rule_name}</p>
                  </div>
                )}
              </div>
            )}

            {/* Provider */}
            {(data.provider || data.provider_id) && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Provider</p>
                <p className="font-medium">{data.provider?.name || data.provider_id}</p>
                {data.provider_message_id && (
                  <p className="text-xs text-gray-400 font-mono mt-1">ID: {data.provider_message_id}</p>
                )}
              </div>
            )}

            {/* Rendered Message */}
            {(data.rendered_message || data.message_preview) && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Messaggio</p>
                <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                  <pre className="text-sm whitespace-pre-wrap font-sans text-gray-700">
                    {data.rendered_message || data.message_preview}
                  </pre>
                </div>
              </div>
            )}

            {/* Fallback info */}
            {data.fallback_used && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm font-medium text-amber-800 flex items-center gap-1">
                  <ArrowRight className="w-4 h-4" />
                  Fallback utilizzato
                </p>
                {data.fallback_from && (
                  <p className="text-xs text-amber-700 mt-1">
                    Da: {data.fallback_from} → {data.fallback_to || data.channel}
                  </p>
                )}
                {data.fallback_reason && (
                  <p className="text-xs text-amber-600 mt-1">Motivo: {data.fallback_reason}</p>
                )}
              </div>
            )}

            {/* Retry history */}
            {data.retry_history?.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-2">Cronologia tentativi</p>
                <div className="space-y-2">
                  {data.retry_history.map((retry, i) => (
                    <div key={i} className="text-sm p-2 bg-orange-50 rounded border border-orange-100">
                      <p className="text-orange-700">
                        Tentativo #{i + 1} - {formatDate(retry.timestamp)}
                      </p>
                      {retry.error && <p className="text-xs text-orange-600">{retry.error}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error */}
            {data.last_error && (
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm font-medium text-red-800 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Errore
                </p>
                <p className="text-xs text-red-700 mt-1">{data.last_error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t">
              {(data.rendered_message || data.message_preview) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(data.rendered_message || data.message_preview);
                    toast.success('Messaggio copiato!');
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copia messaggio
                </Button>
              )}
              {data.booking?.id && (
                <Button
                  variant="outline"
                  onClick={() => window.location.href = `/admin/bookings?id=${data.booking.id}`}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Apri prenotazione
                </Button>
              )}
              {['failed', 'cancelled', 'skipped'].includes(data.status) && (
                <Button
                  variant="default"
                  className="bg-orange-600 hover:bg-orange-700"
                  onClick={() => onResend?.(data)}
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reinvia
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Main Component
export default function CommunicationTimeline({
  bookingId,
  guestId,
  limit = 20,
  compact = false,
  showQuickActions = true,
  onMessageClick
}) {
  const { token } = useAuth();
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [metadata, setMetadata] = useState({});
  const [filters, setFilters] = useState({
    channel: '',
    status: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchTimeline = useCallback(async () => {
    if (!token) return;
    if (!bookingId && !guestId) return;

    setLoading(true);
    setError(null);

    try {
      let url;
      if (bookingId) {
        url = `${API}/admin/communication/timeline/booking/${bookingId}`;
      } else if (guestId) {
        url = `${API}/admin/communication/timeline/guest/${guestId}`;
      }

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        throw new Error('Errore nel caricamento della timeline');
      }

      const data = await res.json();
      setTimeline(data.timeline || []);
      setMetadata({
        bookingCode: data.booking_code,
        guestName: data.guest_name,
        bookingSource: data.booking_source,
        communicationStatus: data.communication_status,
        totalCommunications: data.total_communications,
        totalBookings: data.total_bookings,
        bookings: data.bookings
      });
    } catch (e) {
      console.error('Timeline fetch error:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, bookingId, guestId]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success('Messaggio copiato!');
  };

  const handleResend = async (event) => {
    if (!event?.id) return;
    
    const confirmed = window.confirm('Sei sicuro di voler reinviare questo messaggio?');
    if (!confirmed) return;

    try {
      const res = await fetch(`${API}/admin/communication/resend/${event.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Messaggio accodato per il reinvio!');
        fetchTimeline(); // Refresh
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Errore nel reinvio');
      }
    } catch (e) {
      toast.error('Errore nel reinvio del messaggio');
    }
  };

  const handleViewDetails = (event) => {
    setSelectedEvent(event);
    setShowDetails(true);
    onMessageClick?.(event);
  };

  // Filter timeline
  const filteredTimeline = timeline.filter(event => {
    if (filters.channel && event.channel !== filters.channel) return false;
    if (filters.status && event.status !== filters.status) return false;
    return true;
  }).slice(0, limit);

  // Get unique channels and statuses for filters
  const channels = [...new Set(timeline.map(e => e.channel).filter(Boolean))];
  const statuses = [...new Set(timeline.map(e => e.status).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="timeline-loading">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Caricamento timeline...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-6" data-testid="timeline-error">
        <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-sm text-red-600">{error}</p>
        <Button variant="outline" size="sm" className="mt-2" onClick={fetchTimeline}>
          Riprova
        </Button>
      </div>
    );
  }

  return (
    <div className="communication-timeline" data-testid="communication-timeline">
      {/* Header with metadata */}
      {!compact && metadata.guestName && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-500" />
              <span className="font-medium">{metadata.guestName}</span>
              {metadata.bookingCode && (
                <span className="text-sm text-gray-500">• {metadata.bookingCode}</span>
              )}
              {metadata.bookingSource && (
                <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                  {metadata.bookingSource.display_name || metadata.bookingSource.source}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">
                {metadata.totalCommunications || 0} comunicazioni
              </span>
              <Button variant="ghost" size="sm" onClick={fetchTimeline}>
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? 'bg-gray-200' : ''}
              >
                Filtri
              </Button>
            </div>
          </div>

          {/* Communication status summary */}
          {metadata.communicationStatus && !compact && (
            <div className="flex gap-3 mt-3 flex-wrap">
              {metadata.communicationStatus.whatsapp_sent && (
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" />
                  WhatsApp inviato
                </span>
              )}
              {metadata.communicationStatus.push_sent && (
                <span className="text-xs text-purple-600 flex items-center gap-1">
                  <Bell className="w-3 h-3" />
                  Push inviato
                </span>
              )}
              {metadata.communicationStatus.email_sent && (
                <span className="text-xs text-blue-600 flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  Email inviato
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="mb-4 p-3 bg-white border rounded-lg flex gap-4 flex-wrap">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Canale</label>
            <select
              value={filters.channel}
              onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
              className="text-sm border rounded px-2 py-1"
            >
              <option value="">Tutti</option>
              {channels.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Stato</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="text-sm border rounded px-2 py-1"
            >
              <option value="">Tutti</option>
              {statuses.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilters({ channel: '', status: '' })}
            className="self-end"
          >
            Resetta
          </Button>
        </div>
      )}

      {/* Timeline events */}
      {filteredTimeline.length === 0 ? (
        <div className="text-center py-8" data-testid="timeline-empty">
          <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Nessuna comunicazione trovata</p>
          {(filters.channel || filters.status) && (
            <Button
              variant="link"
              size="sm"
              onClick={() => setFilters({ channel: '', status: '' })}
              className="mt-2"
            >
              Rimuovi filtri
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-0 ml-2" data-testid="timeline-events">
          {filteredTimeline.map((event, index) => (
            <TimelineEvent
              key={event.id || index}
              event={event}
              compact={compact}
              onViewDetails={showQuickActions ? handleViewDetails : undefined}
              onCopy={showQuickActions ? handleCopy : undefined}
              onResend={showQuickActions ? handleResend : undefined}
            />
          ))}
        </div>
      )}

      {/* Load more */}
      {timeline.length > limit && (
        <div className="text-center mt-4">
          <Button variant="outline" size="sm" onClick={() => {}}>
            Carica altro ({timeline.length - limit} rimanenti)
          </Button>
        </div>
      )}

      {/* Message Details Modal */}
      <MessageDetailsModal
        event={selectedEvent}
        open={showDetails}
        onClose={() => setShowDetails(false)}
        onResend={handleResend}
      />
    </div>
  );
}

// Named export for sub-components
export { ChannelIcon, StatusBadge, TimelineEvent };
